"use client";

import { cn } from "../../lib/utils";
import { JobCard, type JobData } from "./job-card";

export type PostStudioJobsSectionProps = {
  jobs: JobData[];
  selectedJobIds: string[];
  allSelected: boolean;
  title: string;
  subtitle: string;
  selectAllLabel: string;
  clearAllLabel: string;
  emptyLabel?: string;
  onToggleJob: (job: JobData) => void;
  onToggleSelectAll: () => void;
  getSelectionLabel: (isSelected: boolean, title: string) => string;
  className?: string;
};

export function PostStudioJobsSection({
  jobs,
  selectedJobIds,
  allSelected,
  title,
  subtitle,
  selectAllLabel,
  clearAllLabel,
  emptyLabel = "No jobs found matching your filters.",
  onToggleJob,
  onToggleSelectAll,
  getSelectionLabel,
  className,
}: PostStudioJobsSectionProps) {
  return (
    <section
      className={cn(
        "rounded-2xl border border-black bg-white px-4 py-4 shadow-[0px_4px_0px_0px_#000000]",
        className
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-[#344054]">{title}</h2>
          <p className="text-xs leading-5 text-[#667085]">{subtitle}</p>
        </div>
        {jobs.length > 0 && (
          <button
            type="button"
            onClick={onToggleSelectAll}
            className="inline-flex min-h-8 shrink-0 items-center rounded-lg border border-[#0D41E1] bg-white px-3 text-xs font-medium text-[#0D41E1]"
          >
            {allSelected ? clearAllLabel : selectAllLabel}
          </button>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {jobs.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-black/10 bg-black/5 py-16 text-center opacity-50">
            <p className="font-medium text-[#344054]">{emptyLabel}</p>
          </div>
        )}

        {jobs.map((job) => {
          const isSelected = selectedJobIds.includes(job.id);

          return (
            <div key={job.id} className="relative">
              <button
                type="button"
                onClick={() => onToggleJob(job)}
                className="absolute inset-0 z-10 rounded-lg"
                aria-label={getSelectionLabel(isSelected, job.title)}
              />
              <div className="pointer-events-none absolute right-3 top-3 z-20 flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full border transition-colors",
                    isSelected ? "border-[#ED5AA9] bg-[#ED5AA9]" : "border-[#98A2B3] bg-white"
                  )}
                >
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full bg-white transition-opacity",
                      isSelected ? "opacity-100" : "opacity-0"
                    )}
                  />
                </span>
              </div>
              <JobCard
                job={job}
                hideHeader
                selected={isSelected}
                onSelect={() => onToggleJob(job)}
                className="pr-12"
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
